import { emit, LOST_CONNECTION } from './events';
import { gql } from './client';
import { runEvent, runEventForResults } from '../pageUtils';
import { CONFIG } from '../config';
import { candidatesFrom, planFrom } from './locateCandidates';

export type Confidence = 'High' | 'Medium' | 'Low' | 'Not established';
export type ConstraintStatus = 'Present' | 'Absent' | 'Not checked';

/** Scheduled departures at one bus stop on the timetable's reference days. */
export interface BusFigures {
  weekday: number;
  /** Weekday departures between 07:00 and 19:00. */
  daytime: number;
  /** Departures in the busiest clock hour between 07:00 and 19:00. */
  peakHour: number;
  /** Distinct operator + route number combinations. */
  services: number;
  saturday: number;
  sunday: number;
}

export interface BusServiceSummary {
  /** 'none': timetables cover the stop but no bus is scheduled. 'unknown': no timetable data covers it. */
  nearest: BusFigures | 'none' | 'unknown';
  /** A clearly better-served stop within 800 m, when there is one. */
  best?: BusFigures & { name: string; distanceM: number };
  /** ISO date of the reference Wednesday. */
  timetableDate: string;
}

export interface SiteConstraint {
  dataset: string;
  label: string;
  status: ConstraintStatus;
  count: number;
  names: string[];
  reason?: string;
  group?: string;
  patchy?: boolean;
  /** Only a marginal overlap: the share of the outline it covers is below the marginal threshold (see `coveragePct`), a placement-tolerance shift of the outline would clear it, or it was found only as a sliver at the edge. Not treated as within the designation. */
  edgeOnly?: boolean;
  /** Area designations, when the outline is known: the percentage (0 to 100) of the traced outline the designation covers. */
  coveragePct?: number;
  /** Transport rows: straight-line distance to the nearest stop or station, from the site boundary when `measuredFrom` is 'boundary' (0 means inside it), else from the pin. */
  distanceM?: number;
  measuredFrom?: 'boundary' | 'pin';
  /** Transport rows with an outline: the same stop's distance from the site centre. */
  centreDistanceM?: number;
  within400M?: number;
  within800M?: number;
  /** Bus stop row only, when timetable data is loaded. */
  busService?: BusServiceSummary;
  /** Transport rows: the stops or stations behind the figures. Absent on cases saved before this was recorded. */
  places?: TransportPlace[];
  /** Listed buildings: the individual entries found, nearest first (up to 60; `count` may be higher). Absent on cases saved before this was recorded. */
  features?: ConstraintFeature[];
}

export interface ConstraintFeature {
  name: string;
  lat: number;
  lon: number;
  /** From the site boundary when one has been traced (0 means inside it), else from the pin. */
  distanceM: number;
  /** Secondary line under the name, for example a brownfield site's area and dwelling capacity. */
  detail?: string;
}

export interface TransportPlace {
  name: string;
  lat: number;
  lon: number;
  distanceM: number;
  /** Bus stops only: how often buses call here ('unknown' when the timetables do not cover the stop). Absent on cases saved before this was recorded. */
  service?: BusFigures | 'none' | 'unknown';
}

export interface PhaseUsage {
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  model?: string;
  /** Premium OS map tiles charged while the site was marked, and their cost at OS's rate. */
  osMapTiles?: number;
  osMapCostGbp?: number;
}

export interface BoundaryEvidence { label: string; status: string; detail: string }

/** A site outline read from a Location Plan and placed on the map, or ('drawn') drawn on the map by the applicant. */
export interface SiteBoundary {
  /** Closed ring of [lon, lat]. */
  ring: [number, number][];
  /** Further red-line areas when the plan's site is split; `areaHa` already includes them. */
  parts?: [number, number][][];
  areaHa: number;
  placement: 'matched' | 'seed' | 'drawn';
  confidence: Confidence;
  documentName?: string;
  scaleDenominator?: number;
  evidence: BoundaryEvidence[];
  warnings: string[];
  userEdited: boolean;
  /** How far the user resized and turned the outline from the one read off the plan; absent when untouched. */
  adjust?: { scale: number; rotationDeg: number };
  /** [lon, lat] of the outline's centre as first read off the plan, so a moved outline can be put back. */
  origin?: [number, number];
  /** The location plan's own drawing and where its corners sit on the ground, as first read; absent for older cases. */
  plan?: PlanOverlay;
}

export interface PlanOverlay {
  documentId: string;
  /** 1-based page of the document the map frame is on. */
  page: number;
  /** The map frame in page points at pdf.js viewport scale 1: [x0, y0, x1, y1]. */
  frame: [number, number, number, number];
  /** [lon, lat] of the frame's top-left, top-right, bottom-right and bottom-left corners. */
  corners: [number, number][];
}

/** Another place the documents plausibly describe, offered when the first guess is wrong. */
export interface LocateAlternative {
  lat: number;
  lon: number;
  label: string;
  distanceM: number | null;
  direction: string | null;
}

export interface LocateResult {
  lat: number | null;
  siteAreaHectares: number | null;
  lon: number | null;
  radiusM: number;
  confidence: Confidence;
  siteName: string | null;
  /** What the documents say is proposed, in a sentence or two. */
  summary: string | null;
  /** Street names the documents give for the site; used to look up house numbers printed on the location plan. */
  streets: string[];
  /** The site's own postal address (with its postcode), checked against where the location plan's outline is placed. */
  siteAddress: string | null;
  basis: string[];
  quotes: string[];
  warnings: string[];
  constraints: SiteConstraint[];
  gazetteerVersion: string | null;
  method: string;
  alternatives: LocateAlternative[];
  usage: PhaseUsage | null;
  /** The local planning authority the documents name, if they name one (checked against the one the site is in). */
  namedAuthority?: string | null;
}

export interface SiteLocationInput {
  lat: number;
  lon: number;
  radiusM: number;
  confidence: Confidence;
  source: string;
  evidence: string[];
  warnings: string[];
  constraints: SiteConstraint[];
  summary?: string;
  siteName?: string;
  gazetteerVersion?: string;
  usage?: PhaseUsage;
  userEdited: boolean;
  boundary?: SiteBoundary;
}

/** A case's saved `siteLocation` JSON, or null when there is none or it has no usable position. */
export function parseSavedLocation(json: string | null | undefined): SiteLocationInput | null {
  if (!json) return null;
  let raw: Partial<SiteLocationInput> | null;
  try { raw = JSON.parse(json) as Partial<SiteLocationInput> | null; } catch { return null; }
  if (!raw || typeof raw.lat !== 'number' || typeof raw.lon !== 'number' || typeof raw.radiusM !== 'number') return null;
  const ring = raw.boundary?.ring;
  const boundary = Array.isArray(ring) && ring.length >= 4 ? raw.boundary : undefined;
  return {
    lat: raw.lat, lon: raw.lon, radiusM: raw.radiusM,
    confidence: raw.confidence ?? 'Not established',
    source: raw.source ?? 'unknown',
    evidence: Array.isArray(raw.evidence) ? raw.evidence : [],
    warnings: Array.isArray(raw.warnings) ? raw.warnings : [],
    constraints: Array.isArray(raw.constraints) ? raw.constraints : [],
    summary: raw.summary, siteName: raw.siteName, gazetteerVersion: raw.gazetteerVersion, usage: raw.usage,
    userEdited: raw.userEdited === true,
    boundary: boundary ? {
      ...boundary,
      parts: Array.isArray(boundary.parts) && boundary.parts.length ? boundary.parts : undefined,
      evidence: Array.isArray(boundary.evidence) ? boundary.evidence : [],
      warnings: Array.isArray(boundary.warnings) ? boundary.warnings : [],
      userEdited: boundary.userEdited === true,
    } : undefined,
  };
}

export const LOCATE_ENABLED =(): boolean => !!CONFIG.gazetteerUrl;

interface LocateSiteResponse {
  clues?: { siteName?: string | null; summary?: string | null; evidence?: string[]; siteAreaHectares?: number | null; streets?: unknown; siteAddress?: string | null; sitePostcode?: string | null; localPlanningAuthority?: string | null };
  resolved?: { method?: string; lat?: number | null; lon?: number | null; basis?: string[]; alternatives?: { lat?: number; lon?: number; label?: string; distanceM?: number; direction?: string }[] };
  radiusM?: number;
  constraints?: SiteConstraint[];
  confidence?: Confidence;
  warnings?: string[];
  gazetteerVersion?: string | null;
  usage?: PhaseUsage;
}

function stepData<T>(results: Record<string, unknown>, step: string, what: string): T {
  const data = (results[step] as { data?: T } | undefined)?.data;
  if (!data) throw new Error(`${what} returned nothing`);
  return data;
}

function siteAddressOf(address: string | null | undefined, postcode: string | null | undefined): string | null {
  const a = address?.trim();
  if (!a) return null;
  const pc = postcode?.trim();
  return pc && !a.toUpperCase().replace(/\s/g, '').includes(pc.toUpperCase().replace(/\s/g, '')) ? `${a}, ${pc}` : a;
}

// Locating can take a while: it reads the documents, asks the model for clues and queries planning.data.gov.uk.
export async function locateSite(caseId: string): Promise<LocateResult> {
  const results = await retryOnDrop(() => runEventForResults(connId => emit('LocateSite', caseId, {}, connId), 90_000));
  const r = stepData<LocateSiteResponse>(results, 'locate', 'The location lookup');
  return {
    lat: r.resolved?.lat ?? null,
    siteAreaHectares: typeof r.clues?.siteAreaHectares === 'number' ? r.clues.siteAreaHectares : null,
    lon: r.resolved?.lon ?? null,
    radiusM: r.radiusM ?? 250,
    confidence: r.confidence ?? 'Not established',
    siteName: r.clues?.siteName ?? null,
    summary: r.clues?.summary ?? null,
    namedAuthority: r.clues?.localPlanningAuthority?.trim() || null,
    streets: Array.isArray(r.clues?.streets) ? r.clues.streets.filter((s): s is string => typeof s === 'string') : [],
    siteAddress: siteAddressOf(r.clues?.siteAddress ?? r.clues?.siteName, r.clues?.sitePostcode),
    basis: r.resolved?.basis ?? [],
    quotes: r.clues?.evidence ?? [],
    warnings: r.warnings ?? [],
    constraints: r.constraints ?? [],
    gazetteerVersion: r.gazetteerVersion ?? null,
    method: r.resolved?.method ?? 'none',
    alternatives: (r.resolved?.alternatives ?? []).flatMap(a => typeof a.lat === 'number' && typeof a.lon === 'number' && a.label
      ? [{ lat: a.lat, lon: a.lon, label: a.label, distanceM: a.distanceM ?? null, direction: a.direction ?? null }] : []),
    usage: r.usage ?? null,
  };
}

interface LocateBoundaryResponse {
  boundary?: { ring?: [number, number][]; parts?: [number, number][][]; areaHa?: number } | null;
  placement?: 'matched' | 'seed' | 'none';
  confidence?: Confidence;
  evidence?: BoundaryEvidence[];
  warnings?: string[];
  scaleDenominator?: number | null;
  documentName?: string | null;
  documentId?: string | null;
  page?: number | null;
  overlay?: { frame?: number[]; corners?: [number, number][] } | null;
  candidates?: {
    boundary?: { ring?: [number, number][]; parts?: [number, number][][]; areaHa?: number } | null;
    overlay?: { frame?: number[]; corners?: [number, number][] } | null;
    source?: string | null;
    score?: number | null;
  }[] | null;
  /** Present when the plan-matching route handed back a kick-off ack instead of the final result. */
  id?: string;
  async?: boolean;
}

interface LocateBoundaryRunRow {
  id: string;
  status: 'Running' | 'Completed' | 'Failed';
  stage?: string | null;
  progressPct?: number | null;
  resultJson?: string | null;
  error?: string | null;
}

const LOCATE_BOUNDARY_RUN_FIELDS = 'id status stage progressPct resultJson error';

async function getLocateBoundaryRun(id: string): Promise<LocateBoundaryRunRow | undefined> {
  const data = await gql<{ elevationsPlanningLocateBoundaryRun: LocateBoundaryRunRow | null }>(
    `query GetLocateBoundaryRun($id: ID!) { elevationsPlanningLocateBoundaryRun(id: $id) { ${LOCATE_BOUNDARY_RUN_FIELDS} } }`,
    { id },
  );
  return data.elevationsPlanningLocateBoundaryRun ?? undefined;
}

const LOCATE_BOUNDARY_POLL_MS = 1_500;
// Matches the Lambda's own async-worker ceiling (see ep-planning-locate-service-stack.ts): the run row
// itself would otherwise be polled forever on a stuck match.
const LOCATE_BOUNDARY_MAX_WAIT_MS = 5 * 60_000;

/** Polls a kicked-off boundary-matching run to completion, reporting each stage change along the way. */
async function waitForLocateBoundaryRun(id: string, onProgress?: (pct: number, stage: string) => void): Promise<LocateBoundaryResponse> {
  const started = Date.now();
  for (;;) {
    const run = await getLocateBoundaryRun(id);
    if (run) {
      if (run.status === 'Completed') return run.resultJson ? (JSON.parse(run.resultJson) as LocateBoundaryResponse) : {};
      if (run.status === 'Failed') throw new Error(run.error ?? 'The location plan could not be read.');
      if (typeof run.progressPct === 'number' && run.stage) onProgress?.(run.progressPct, run.stage);
    }
    if (Date.now() - started > LOCATE_BOUNDARY_MAX_WAIT_MS) throw new Error('The location plan lookup is taking too long.');
    await new Promise((resolve) => setTimeout(resolve, LOCATE_BOUNDARY_POLL_MS));
  }
}

/** The same outline at another place that fits the map or the land parcels. */
export interface BoundaryCandidate {
  boundary: SiteBoundary;
  source: 'map' | 'parcels';
}

/** What reading the case's Location Plan produced. `boundary` is null when there is no plan or nothing usable. */
export interface BoundaryResult {
  boundary: SiteBoundary | null;
  /** Other places the outline might sit, most likely first, when the first is not certain. */
  candidates: BoundaryCandidate[];
  /**
   * When the plan could not be matched to the map: its outline and drawing at the printed scale, centred on the location
   * clue, for the user to place by hand. Never a found outline; null when there is a boundary or nothing to place.
   */
  unplaced: SiteBoundary | null;
  /** Why there is no boundary, or caveats about the one found. */
  warnings: string[];
}

/**
 * Reads the case's Location Plan (if it has one) and traces the site outline onto the map. Never throws for "no plan".
 * Matching can take a while on a large or awkwardly-scanned sheet, so the triggering workflow only ever hands back a
 * kick-off ack straight away (`r.async`); the real result is then polled for from the run row it created, reporting
 * each stage along the way via `onProgress`, which is best-effort — a page that ignores it just sees no progress bar.
 */
export async function locateBoundary(
  caseId: string,
  seed: { lat: number; lon: number; confidence: Confidence; pin?: boolean } | null,
  statedHectares: number | null,
  streets: readonly string[] = [],
  onProgress?: (pct: number, stage: string) => void,
  siteAddress?: string | null,
  siteName?: string | null,
): Promise<BoundaryResult> {
  const payload: Record<string, unknown> = {};
  if (seed) payload.seed = seed;
  if (statedHectares != null && statedHectares > 0) payload.statedHectares = statedHectares;
  if (streets.length || siteAddress) payload.addressHints = { streets: [...streets], ...(siteAddress ? { siteAddress } : {}), ...(siteAddress && siteName ? { siteName } : {}) };
  const results = await retryOnDrop(() => runEventForResults(connId => emit('LocateBoundary', caseId, payload, connId), 90_000));
  const ack = stepData<LocateBoundaryResponse>(results, 'boundary', 'The location plan lookup');
  const r = ack.async && ack.id ? await waitForLocateBoundaryRun(ack.id, onProgress) : ack;
  const ring = r.boundary?.ring;
  const traced = Array.isArray(ring) && ring.length >= 4 && (r.placement === 'matched' || r.placement === 'seed');
  if (!traced || !r.boundary || !ring) return { boundary: null, candidates: [], unplaced: null, warnings: r.warnings ?? [] };
  const plan = planFrom(r);
  const boundary: SiteBoundary = {
    ring,
    ...(r.boundary.parts?.length ? { parts: r.boundary.parts } : {}),
    areaHa: r.boundary.areaHa ?? 0,
    placement: r.placement as 'matched' | 'seed',
    confidence: r.confidence ?? 'Low',
    documentName: r.documentName ?? undefined,
    scaleDenominator: r.scaleDenominator ?? undefined,
    evidence: r.evidence ?? [],
    warnings: r.warnings ?? [],
    userEdited: false,
    ...(plan ? { plan } : {}),
  };
  // An outline the plan could not be matched for is only a guess at the location clue: offered for placing by hand, with
  // its drawing to place it by, and never as a found outline.
  if (r.confidence === 'Not established') {
    return { boundary: null, candidates: [], unplaced: plan && r.scaleDenominator ? boundary : null, warnings: r.warnings ?? [] };
  }
  return { candidates: candidatesFrom(r, r.boundary.areaHa ?? 0), boundary, unplaced: null, warnings: r.warnings ?? [] };
}

/** How far the designation search is widened around an outline, to allow for tracing error. A less certain placement is widened more, but the search always follows the outline. */
export function boundaryToleranceM(c: Confidence): number {
  if (c === 'High') return 10;
  if (c === 'Medium') return 50;
  return 100;
}

// The lookups are read-only, so when the socket drops mid-request (the result is lost with it) asking again on the new connection is safe.
async function retryOnDrop<T>(run: () => Promise<T>): Promise<T> {
  try { return await run(); } catch (e) {
    if (!(e instanceof Error) || e.message !== LOST_CONNECTION) throw e;
    return run();
  }
}

export async function refreshConstraints(
  caseId: string, lat: number, lon: number, radiusM: number, outline?: { ring: [number, number][]; parts?: [number, number][][]; toleranceM: number }, view = false,
): Promise<SiteConstraint[]> {
  const payload: Record<string, unknown> = { lat, lon, radiusM };
  if (view) payload.view = true;
  if (outline) {
    payload.ring = outline.ring; payload.toleranceM = outline.toleranceM;
    // A site drawn in several pieces is checked against designations over every piece.
    if (outline.parts?.length) payload.parts = outline.parts;
  }
  // 90 s: a lookup that times out is tried once more on the server, which can double a slow check.
  const results = await retryOnDrop(() => runEventForResults(connId => emit('RefreshSiteConstraints', caseId, payload, connId), 90_000));
  return stepData<{ constraints?: SiteConstraint[] }>(results, 'constraints', 'The designation lookup').constraints ?? [];
}

/** With nameFromAddress, the workflow looks up the address at the site's point and names the case from it. */
export function saveSiteLocation(caseId: string, location: SiteLocationInput, performedBy?: string, opts: { nameFromAddress?: boolean } = {}): Promise<void> {
  const address = opts.nameFromAddress ? { nameFromAddress: true, lat: location.lat, lon: location.lon } : {};
  return runEvent(connId => emit('SetSiteLocation', caseId, { siteLocation: JSON.stringify(location), performedBy, ...address }, connId), 30_000);
}
